import { useMutation } from "@tanstack/react-query";
import { apiClient } from "@finitless/shared/lib";
import { isEndpointEnabled } from "@finitless/shared/config";
import { buildQueryParams } from "../lib/globalUtils";
import { mapUiLocaleToApiLanguage } from "../i18n/config";
import { useUiLocale } from "./useUiLocale";

/**
 * Hook to download Executive Monthly Report (PDF)
 * @param {Object} params - Query parameters
 * @param {string} params.month - Month in YYYY-MM format (e.g., 2025-10)
 * @param {number} params.business_id - Filter by specific business ID (optional)
 * @param {number} params.franchise_id - Filter by specific franchise ID (optional)
 * @param {string} params.language - Report language code (optional, default: 'en')
 */
export const useDownloadExecutiveReport = () => {
  const { locale } = useUiLocale();
  const defaultLanguage = mapUiLocaleToApiLanguage(locale);

  return useMutation({
    mutationFn: async ({
      month,
      business_id,
      franchise_id,
      language = defaultLanguage,
    }) => {
      if (!month) {
        throw new Error("Month parameter is required");
      }

      // Validate month format (YYYY-MM)
      const monthRegex = /^\d{4}-\d{2}$/;
      if (!monthRegex.test(month)) {
        throw new Error("Month must be in YYYY-MM format");
      }

      const resolvedLanguage = mapUiLocaleToApiLanguage(language);

      const params = {
        month,
        ...(business_id && { business_id }),
        ...(franchise_id && { franchise_id }),
        language: resolvedLanguage,
      };

      const queryString = buildQueryParams(params);
      const response = await apiClient.get(
        `/ai/reports/executive/download${queryString ? `?${queryString}` : ""}`,
        {
          responseType: "blob", // Important for PDF downloads
        },
      );

      return response;
    },
    enabled: isEndpointEnabled("reports", "executive"),
  });
};

/**
 * Hook to download Performance Report (PDF)
 * @param {Object} params - Query parameters
 * @param {string} params.month - Month in YYYY-MM format (e.g., 2025-10)
 * @param {number} params.business_id - Filter by specific business ID (optional)
 * @param {number} params.franchise_id - Filter by specific franchise ID (optional)
 * @param {string} params.language - Report language code (optional, default: 'en')
 */
export const useDownloadPerformanceReport = () => {
  const { locale } = useUiLocale();
  const defaultLanguage = mapUiLocaleToApiLanguage(locale);

  return useMutation({
    mutationFn: async ({
      month,
      business_id,
      franchise_id,
      language = defaultLanguage,
    }) => {
      if (!month) {
        throw new Error("Month parameter is required");
      }

      // Validate month format (YYYY-MM)
      const monthRegex = /^\d{4}-\d{2}$/;
      if (!monthRegex.test(month)) {
        throw new Error("Month must be in YYYY-MM format");
      }

      const resolvedLanguage = mapUiLocaleToApiLanguage(language);

      const params = {
        month,
        ...(business_id && { business_id }),
        ...(franchise_id && { franchise_id }),
        language: resolvedLanguage,
      };

      const queryString = buildQueryParams(params);
      const response = await apiClient.get(
        `/ai/reports/performance/download${queryString ? `?${queryString}` : ""}`,
        {
          responseType: "blob", // Important for PDF downloads
        },
      );

      return response;
    },
    enabled: isEndpointEnabled("reports", "performance"),
  });
};

export default {
  useDownloadExecutiveReport,
  useDownloadPerformanceReport,
};
